import { Extension, type JSONContent } from "@tiptap/core";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import StarterKit from "@tiptap/starter-kit";
import { Plugin, type Transaction } from "@tiptap/pm/state";
import { createBlockId } from "./sanitize";

declare const NotesDocumentBrand: unique symbol;
export type NotesDocumentJson = JSONContent & {
  readonly [NotesDocumentBrand]: "notes-document";
};

export const ALLOWED_NOTE_LINK_SCHEMES = ["http", "https", "mailto", "tel"] as const;


export const TOP_LEVEL_BLOCK_NODE_TYPES = [
  "paragraph",
  "heading",
  "blockquote",
  "codeBlock",
  "bulletList",
  "orderedList",
  "taskList",
  "horizontalRule",
] as const;

export type TopLevelBlockType = (typeof TOP_LEVEL_BLOCK_NODE_TYPES)[number];

export const TOP_LEVEL_BLOCK_TYPE_SET: ReadonlySet<TopLevelBlockType> = new Set(TOP_LEVEL_BLOCK_NODE_TYPES);

/**
 * Attaches a stable `blockId` to every top-level block. Two minting sites
 * cooperate:
 *
 *   1. The editor plugin below, for user-typed blocks. Without it,
 *      ProseMirror's `default: null` leaks into state and every
 *      `normalizeNotesDocument` call mints a fresh id for the same
 *      content — blockIds would churn on every parse instead of staying
 *      stable across edits.
 *   2. `normalizeTopLevelBlockWithMint`, for non-editor entrypoints
 *      (AI output, markdown imports). These never pass through the plugin.
 *
 * The plugin also dedupes duplicate ids, which ProseMirror can produce
 * when the user splits a block (Enter mid-paragraph clones attrs onto
 * both halves).
 */
export const BlockIdExtension = Extension.create({
  name: "blockId",
  addGlobalAttributes() {
    return [
      {
        types: [...TOP_LEVEL_BLOCK_NODE_TYPES],
        attributes: {
          blockId: {
            default: null,
            parseHTML: (element) => element.getAttribute("data-block-id"),
            renderHTML: (attributes) => {
              if (!attributes.blockId) return {};
              return { "data-block-id": attributes.blockId };
            },
          },
        },
      },
    ];
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction: (transactions, _oldState, newState) => {
          // Selection-only transactions (arrow keys, click-to-move,
          // drag-select) dominate an active session; skip the traversal.
          if (!transactions.some((t) => t.docChanged)) return null;

          let tr: Transaction | null = null;
          const seen = new Set<string>();
          newState.doc.forEach((node, offset) => {
            if (!TOP_LEVEL_BLOCK_TYPE_SET.has(node.type.name as TopLevelBlockType)) {
              return;
            }
            const current = node.attrs.blockId;
            if (typeof current !== "string" || current.length === 0 || seen.has(current)) {
              tr = (tr ?? newState.tr).setNodeAttribute(offset, "blockId", createBlockId());
            } else {
              seen.add(current);
            }
          });
          return tr;
        },
      }),
    ];
  },
});

export function createNotesExtensions(options?: { placeholder?: string | null }) {
  const extensions = [
    // `link: false` is load-bearing. StarterKit v3 bundles Link with its
    // own defaults (`openOnClick: true`, `protocols: []`); leaving it on
    // registers the mark twice and Tiptap keeps the StarterKit copy,
    // silently dropping our `Link.configure` below. Save-path Zod still
    // strips `javascript:`, but a pasted one in the editor would navigate.
    StarterKit.configure({
      heading: { levels: [1, 2, 3, 4] },
      link: false,
    }),
    Link.configure({
      protocols: [...ALLOWED_NOTE_LINK_SCHEMES],
      openOnClick: false,
      autolink: true,
      defaultProtocol: "https",
    }),
    TaskList,
    TaskItem.configure({ nested: true }),
    BlockIdExtension,
  ];

  if (options?.placeholder) {
    extensions.push(
      Placeholder.configure({
        placeholder: options.placeholder,
        emptyEditorClass: "is-editor-empty",
      })
    );
  }

  return extensions;
}

export const notesBaseExtensions = createNotesExtensions();
